import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusBanner";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { fmtDate } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import { Truck, ChevronRight } from "lucide-react";

/**
 * Open Orders — card layout, excludes completed/cancelled orders by design.
 * (Completed orders live in the History page.)
 */
export default function OpenOrders() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("open"); // open | in_progress | all-open
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { setPage(1); }, [debouncedQ, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { page, limit: 20 };
    if (status === "open") params.status = "open";
    else if (status === "in_progress") params.status = "in_progress";
    if (debouncedQ) params.q = debouncedQ;
    api.get("/orders", { params })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, status, debouncedQ, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div>
          <div className="label-overline">Operations</div>
          <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">Open Orders</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Live queue — completed orders move to <Link to="/app/history" className="text-blue-700 hover:underline">History</Link>.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select data-testid="orders-status"
                  className="h-10 border border-slate-300 rounded px-2 font-mono"
                  value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="open">open</option>
            <option value="in_progress">in progress</option>
          </select>
          <Input data-testid="orders-q" placeholder="Order # or registration…"
                 className="sm:w-64 rounded h-10 font-mono"
                 value={q} onChange={(e) => setQ(e.target.value)} />
          <Link to="/app/new-entry">
            <Button data-testid="orders-new-cta"
                    className="rounded bg-blue-700 hover:bg-blue-800 h-10 whitespace-nowrap w-full sm:w-auto">
              <Truck size={16} className="mr-2" /> New Truck Entry
            </Button>
          </Link>
        </div>
      </div>

      {loading && (
        <div className="text-slate-500 label-overline">Loading…</div>
      )}
      {!loading && error && (
        <WidgetError
          error={error}
          onRetry={retry}
          title="Could not load open orders"
          testId="open-orders-error"
        />
      )}
      {!loading && !error && data.items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded p-10 text-center">
          <div className="label-overline">No open orders</div>
          <p className="mt-2 text-slate-600">Start with a New Truck Entry.</p>
        </div>
      )}

      {!error && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.items.map((o) => (
          <Link key={o.id} to={`/app/orders/${o.id}`}
                data-testid={`order-card-${o.order_no}`}
                className="card-tile">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-xs text-slate-500">{o.order_no}</div>
                <div className="font-mono font-black text-2xl text-blue-700 mt-1 truncate">
                  {o.registration_no}
                </div>
                <div className="mt-2 text-sm">
                  <div className="text-slate-500 label-overline">Service</div>
                  <div className="font-medium">{o.service_type_snapshot?.label}</div>
                </div>
                <div className="mt-2 text-sm">
                  <div className="text-slate-500 label-overline">Customer</div>
                  <div className="font-medium">
                    {o.customer_snapshot?.display_name || (
                      <span className="text-amber-700">Walk-in {o.walkin_snapshot?.driver_name ? `— ${o.walkin_snapshot.driver_name}` : ""}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <StatusPill status={o.status} />
                <div className="font-mono text-[11px] text-slate-500 text-right">{fmtDate(o.opened_at)}</div>
                <div className="text-blue-700 font-semibold text-sm flex items-center gap-1">
                  Open <ChevronRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      )}

      <div className="mt-4">
        <PaginationBar page={data.page || 1} pages={data.pages || 1} onChange={setPage} />
      </div>
    </div>
  );
}
